<template>
    <div>
        <BulkEditClientPortalDialog
            v-if="bulkEditClientPortalDialogOpen"
            :value="bulkEditClientPortalDialogOpen"
            :account-id="selectedAccount.id"
            :client-id-list="selectedListIds"
            @close="bulkEditClientPortalDialogOpen = false"
            @failed="(ids) => $emit('failed', ids)"
        />
        <BulkActionBar
            :show-bulk-select="false"
            :items="items"
            :selected-list-ids="selectedListIds"
            :is-mobile="$vuetify.breakpoint.smAndDown"
            :items-text="$t('clients')"
            :items-errors-text="$t('client(s) had errors')"
            :items-selected-text="$t('client(s) selected')"
            :offset-y="-6"
            :relative-elem="relativeElem"
            @bulkSelection="showSelectionCheckbox = $event"
        >
            <template v-slot:actions>
                <v-btn
                    v-if="canContractBulkActions"
                    text
                    color="white"
                    @click="onContractActionClick"
                >
                    {{ $t("Contract actions") }}
                </v-btn>
                <v-btn
                    v-if="can_edit_account(get(selectedAccount, 'id'))"
                    text
                    color="white"
                    @click="onEditUsersClick"
                >
                    {{ $t("Edit Members") }}
                </v-btn>
                <v-btn
                    text
                    color="white"
                    @click="onEditJobsClick"
                >
                    {{ $t("Edit jobs") }}
                </v-btn>
                <v-btn
                    v-if="monitoringEnabled"
                    text
                    color="white"
                    @click="onEditMonitoringClick"
                >
                    {{ $t("Edit monitoring") }}
                </v-btn>
                <v-btn
                    v-if="isClientPortalEnabled"
                    text
                    color="white"
                    @click="onClientPortalClick"
                >
                    {{ $t("Client portal") }}
                </v-btn>
                <v-btn
                    v-if="hasIntegrations"
                    text
                    color="white"
                    @click="onRunIntegrationClick"
                >
                    {{ $t("Run integration") }}
                </v-btn>
            </template>
        </BulkActionBar>
    </div>
</template>

<script>
import BulkActionBar from "../../../components/BulkActionBar";
import DialogModule, {actions as dialogActions} from "../../../store/modules/dialog";
import AccountModule from "../../../store/modules/account";
import {get} from "@/helpers/get-set";
import RightsModule from "../../../composables/use-rights";

import BulkEditClientJobsDialog from "./BulkEditClientJobsDialog";
import BulkEditClientPortalDialog from "./BulkEditClientPortalDialog";
import {computed, ref} from "@vue/composition-api";
import {
    FEATURE_CLIENT_BULK_ACTIONS,
    validateFeatureAllowed
} from "@/modules/subscription/allowed_features";

export default {
    name: "BulkActionBarClientslist",
    components: {
        BulkActionBar,
        BulkEditClientJobsDialog,
        BulkEditClientPortalDialog
    },
    props: {
        items: {
            type: Array,
            default: null
        },
        isMobile: {
            type: Boolean,
            default: false
        },
        selectedListIds: {
            type: Array,
            default: () => []
        },
        showBulkSelect: {
            type: Boolean,
            default: true
        },
        selectedAccount: {
            type: Object,
            default: null
        },
        isSelectedAccountPaid: {
            type: Boolean,
            default: null
        },
        hasIntegrations: {
            type: Boolean,
            default: false
        },
        monitoringEnabled: {
            type: Boolean,
            default: false
        },
        canContractBulkActions: {
            type: Boolean,
            default: false
        },
        relativeElem: {
            type: Object,
            default: null
        }
    },
    setup(props, ctx) {
        const {isFeatureEnabled} = AccountModule();
        const {can_edit_account, can_edit_client} = RightsModule();
        const {setDialogOptions, openDialog} = DialogModule();
        const bulkEditClientPortalDialogOpen = ref(false)

        const onEditUsersClick = () => {
            validateFeatureAllowed(FEATURE_CLIENT_BULK_ACTIONS, props.selectedAccount)
            dialogActions.setDialogOptions({
                component: () => import("@/views/Dialog/EditClientUsersForm"),
                payload: {account_id: get(props.selectedAccount, 'id'), client_id_list: props.selectedListIds}
            });
            dialogActions.openDialog();
        };

        const onEditJobsClick = () => {
            validateFeatureAllowed(FEATURE_CLIENT_BULK_ACTIONS, props.selectedAccount)
            dialogActions.setDialogOptions({
                component: () => import("@/views/Client/ClientsList/BulkEditClientJobsDialog"),
                payload: {account_id: get(props.selectedAccount, 'id'), client_id_list: props.selectedListIds}
            });
            dialogActions.openDialog();
        };

        const onRunIntegrationClick = () => {
            setDialogOptions({
                component: () => import("@/views/Dialog/RunIntegrationDialog"),
                payload: {account_id: get(props.selectedAccount, 'id'), client_id_list: props.selectedListIds}
            });
            openDialog();
        };

        const onEditMonitoringClick = () => {
            ctx.emit("monitoring_mode", true)
        }
        const onContractActionClick = () => {
            dialogActions.setDialogOptions({
                component: () => import("@/views/Dialog/ClientListContractActionsDialog"),
                payload: {account_id: get(props.selectedAccount, 'id'), client_id_list: props.selectedListIds}
            });
            dialogActions.openDialog();
        }

        const isClientPortalEnabled = computed(() => {
            return isFeatureEnabled(get(props.selectedAccount, 'id'), "client_portal", false) && can_edit_account(get(props.selectedAccount, 'id'))
        })

        const onClientPortalClick = () => {
            validateFeatureAllowed(FEATURE_CLIENT_BULK_ACTIONS, props.selectedAccount)
            bulkEditClientPortalDialogOpen.value = true
        }

        const canEditSelectedClients = computed(() => {
            for (const clientId of props.selectedListIds) {
                if (!can_edit_client(clientId)) {
                    return false
                }
            }

            return true
        })

        return {
            can_edit_account,
            onEditUsersClick,
            onEditJobsClick,
            onRunIntegrationClick,
            onEditMonitoringClick,
            onContractActionClick,
            isClientPortalEnabled,
            onClientPortalClick,
            bulkEditClientPortalDialogOpen,
            canEditSelectedClients
        }
    }
}

</script>